<template>
  <ProgramKeyIndicatorContainer
    :class="{
      'shadow-none': fullScreen
    }"
  >
    <div class="flex flex-col flex-1 min-h-0">
      <div v-if="!fullScreen" class="text-sm flex items-center justify-between">
        <span>{{ data.label }}</span>
      </div>

      <div ref="legendContainer" class="mb-2" />

      <div class="relative flex-1 min-h-0 overflow-x-auto">
        <div :style="{ minWidth: fullScreen ? chartMinWidth : 0, height: '100%' }">
          <LineChart
            :id="data.label"
            :data="chartData"
            :options="fullScreen ? fullScreenOptions : previewOptions"
          />
        </div>
      </div>
      <div class="text-sm">
        {{ data.extra }}
      </div>
    </div>
  </ProgramKeyIndicatorContainer>
</template>

<script lang="ts" setup>
import {computed} from "vue";
import {Line as LineChart} from 'vue-chartjs';
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Filler,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    ArcElement, ChartData, Point, ChartOptions
} from 'chart.js';
import ChartService from "@/laboratory/services/ChartService";
import ProgramKeyIndicatorContainer from "@/program/components/program-statistics/ProgramKeyIndicatorContainer.vue";
import ProgramKeyIndicatorOptions from "@/program/services/ProgramKeyIndicatorOptions";
import {LineChartMarketStatistic} from "@/program/interfaces/Program";

ChartJS.register(Title, Tooltip, Legend, Filler, BarElement, CategoryScale, LinearScale, ArcElement);

const {fullScreen = false, data} = defineProps<{
    fullScreen?: boolean;
    data: LineChartMarketStatistic;
}>();

const chartData = computed<ChartData<"line", (number | Point | null)[]>>(() => {
    const colors = ChartService.colorPalette();

    return {
        labels: [],
        datasets: Object.entries(data.value).map(([key, value], index) => {
            return {
                label: key,
                borderColor: colors[index % colors.length],
                data: (value as { label: string, x: string, y: number }[]).map(item => ({
                    x: item.label,
                    y: item.y
                })),
            };
        }) as []
    };
});

const chartOptions: ChartOptions<"line"> = {
    ...ProgramKeyIndicatorOptions.chartDefaultOptions,
    interaction: {
        mode: 'index',
        intersect: false,
    },
    scales: {
        x: {
            ticks: {
                autoSkip: true,
                maxRotation: 45,
                minRotation: 0,
            }
        },
        y: {
            ticks: {
                callback: (value: number | string) => {
                    return value;
                }
            }
        }
    },
    elements: {
        line: {
            tension: 0.4
        }
    }
};

const previewOptions: ChartOptions<"line"> = {
    ...chartOptions,
    ...ProgramKeyIndicatorOptions.chartPreviewOptions,
    scales: {
        ...chartOptions.scales,
        x: {
            ...chartOptions.scales?.x,
            ticks: {
                autoSkip: true,
                maxRotation: 45,
                minRotation: 0,
                maxTicksLimit: 10
            }
        }
    }
} as ChartOptions<"line">;

const fullScreenOptions: ChartOptions<"line"> = {
    ...chartOptions,
    ...ProgramKeyIndicatorOptions.chartFullScreenOptions,
    scales: {
        ...chartOptions.scales,
        x: {
            ...chartOptions.scales?.x,
            ticks: {
                autoSkip: true,
                maxRotation: 45,
                minRotation: 0,
                maxTicksLimit: 40
            }
        }
    },
    plugins: {
        ...chartOptions.plugins,
        ...ProgramKeyIndicatorOptions.chartFullScreenOptions.plugins,
        legend: {
            ...ProgramKeyIndicatorOptions.chartFullScreenOptions.plugins!.legend,
            align: 'start',
        },
    }
} as ChartOptions<"line">;

const dataPointCount = computed(() => {
    const dataset = Object.values(data.value)[0];

    return Array.isArray(dataset) ? dataset.length : 0;
});

const chartMinWidth = computed(() => {
    const points = dataPointCount.value;
    const minWidthPerPoint = 20;
    const minWidth = 400;

    return `${Math.max(points * minWidthPerPoint, minWidth)}px`;
});
</script>
